import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  emptyQueryError,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import type {
  ICreateWcwSearchHistoryPayload,
  IWcwSearchHistory,
} from '~/purchase/types';

type Options = {
  list?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IWcwSearchHistory>>,
      IErrorApiResponse
    >
  >;
  create?: UseMutationOptions<
    ISuccessApiResponse<IWcwSearchHistory>,
    IErrorApiResponse,
    ICreateWcwSearchHistoryPayload
  >;
};

export const useManageWcwSearchHistory = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { state } = usePurchaseContext();
  const idCustomer = state.targetCustomer?.id as string;
  const listQuery = useQuery({
    queryKey: PurchaseQueryKeys.wcwSearchHistory.base(idCustomer),
    queryFn: () =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<Array<IWcwSearchHistory>>>(
            `/offers/api/customers/${idCustomer}/wheel-search-history`,
          )
        : emptyQueryError(),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: false,
    ...options.list,
  });
  const createMutation = useMutation({
    mutationFn: (variables) =>
      idCustomer
        ? callCoreApi<ISuccessApiResponse<IWcwSearchHistory>>(
            `/offers/api/customers/${idCustomer}/wheel-search-history`,
            { method: 'POST', body: JSON.stringify(variables) },
          )
        : emptyQueryError(),
    keysToInvalidateOnSuccess: () => [
      PurchaseQueryKeys.wcwSearchHistory.base(idCustomer),
    ],
    retry: false,
    ...options.create,
  });

  return restrictApi(options, {
    list: listQuery,
    create: createMutation,
  });
};
